import { useColorMode } from "@kobalte/core/color-mode";
import {
  createQuery,
  keepPreviousData,
  useQueryClient,
} from "@tanstack/solid-query";
import { createFileRoute, useNavigate } from "@tanstack/solid-router";
import type { ChartData, ChartOptions } from "chart.js";
import type { JSX } from "solid-js";
import { createMemo, createSignal, onMount, Show } from "solid-js";
import { toast } from "solid-sonner";
import { z } from "zod";
import {
  DataTable,
  type ColumnDefExtended,
} from "~/components/data-table/data-table";
import {
  IconLink,
  IconPencil,
  IconTrash,
  IconTruck,
  IconWrench,
} from "~/components/icons";
import { NotFound } from "~/components/not-found";
import { StandardRouteAssociationsPanel } from "~/components/standard-route/detail/associations-panel";
import { StandardRouteDetailCard } from "~/components/standard-route/detail/detail-card";
import { StandardRouteDetailEditCard } from "~/components/standard-route/detail/edit-card";
import { StandardRouteDetailHeader } from "~/components/standard-route/detail/header";
import {
  StandardRouteDetailGrid,
  StandardRouteDetailLeftColumn,
  StandardRouteDetailMode,
  StandardRouteDetailRightCard,
  StandardRouteDetailRightCardHeader,
  StandardRouteDetailRightColumn,
  StandardRouteDetailTabs,
  StandardRouteDetailTabsContent,
  StandardRouteDetailTabsStack,
} from "~/components/standard-route/detail/layout";
import { Button } from "~/components/ui/button";
import { CardContent } from "~/components/ui/card";
import { BarChart } from "~/components/ui/charts";
import { DatePickerField } from "~/components/ui/date-picker-field";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "~/components/ui/dropdown-menu";
import { Col, Grid } from "~/components/ui/grid";
import { MultiSelect } from "~/components/ui/multi-select";
import { TabsList, TabsTrigger } from "~/components/ui/tabs";
import {
  TextField,
  TextFieldErrorMessage,
  TextFieldInput,
  TextFieldLabel,
} from "~/components/ui/text-field";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { AccountsResource } from "~/features/account/account.resource";
import type { Account } from "~/features/account/account.schema";
import { prefetchAssociations } from "~/features/association/association.prefetch";
import {
  businessDetailResource,
  businessesResource,
} from "~/features/business/business.resource";
import { getFixedAssetDetailPageData } from "~/features/fixed-assets/fixed-asset-detail-page";
import { deleteFixedAsset } from "~/features/fixed-assets/fixed-assets.api";
import { useFixedAssetForm } from "~/features/fixed-assets/fixed-assets.form";
import { FixedAssetDetailResource } from "~/features/fixed-assets/fixed-assets.resource";
import {
  DepreciationConventions,
  DepreciationMethods,
} from "~/features/fixed-assets/fixed-assets.schema";
import type { FixedAssetItem } from "~/features/fixed-assets/fixed-assets.server";
import {
  calculateDepreciation,
  DepreciationConvention,
  DepreciationMethod,
} from "~/lib/depreciation/depreciation";
import { withBusinessGuard } from "~/lib/route-guards";
import { formatDisplayCalendarDate } from "~/lib/utils";

export const Route = createFileRoute(
  "/_dashboard/dashboard/fixed-assets/$fixedAssetId",
)({
  staticData: {
    breadcrumb: (m) => m.loaderData?.asset?.name ?? "Fixed Asset",
  },
  validateSearch: z.object({
    mode: z.enum(["edit"]).optional(),
  }),
  // Route-specific 404 message (takes precedence over the dashboard-wide
  // notFoundComponent). Shown when a fixed asset doesn't exist — e.g. it was
  // deleted, or an AI agent linked here by mistake.
  notFoundComponent: () => (
    <NotFound
      title="Fixed asset not found"
      message="This fixed asset doesn't exist. It may have been deleted, or an agent may have linked you here by mistake."
      homeTo="/dashboard/fixed-assets"
      homeLabel="Go to fixed assets"
    />
  ),
  ...withBusinessGuard(),
  loader: async ({ params, context }) => {
    const collection = await context.queryClient.ensureQueryData(
      businessesResource.options(undefined),
    );
    const businessId = collection.activeBusinessId!;

    const [activeBusiness, asset] = await Promise.all([
      context.queryClient.ensureQueryData(
        businessDetailResource.options(businessId),
      ),
      params.fixedAssetId !== "new"
        ? getFixedAssetDetailPageData({
            data: {
              businessId,
              fixedAssetId: params.fixedAssetId,
            },
          }).then((data) => {
            // Seed the resource cache so the detail query resolves from cache
            // on first render (mirrors the document detail page pattern).
            FixedAssetDetailResource.seed(
              context.queryClient,
              { businessId, id: params.fixedAssetId },
              data.fixedAsset,
            );
            return data.fixedAsset;
          })
        : (context.queryClient.setQueryData(
            FixedAssetDetailResource.key({
              businessId,
              id: params.fixedAssetId,
            }),
            null,
          ),
          null),
    ]);

    // Prefetch the Associations panel queries so they resolve from cache on
    // mount instead of flashing loading states. Fire-and-forget (not awaited)
    // so it never blocks the loader's critical path or SSR.
    if (params.fixedAssetId !== "new") {
      prefetchAssociations(
        context.queryClient,
        businessId,
        "fixed-asset",
        params.fixedAssetId,
      );
    }

    return { businessId, activeBusiness, asset };
  },
  component: FixedAssetDetailPage,
});

type Mode = "view" | "edit";

function FixedAssetDetailPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const params = Route.useParams();
  const data = Route.useLoaderData();

  businessDetailResource.seedIfEmpty(
    qc,
    data().businessId,
    data().activeBusiness,
  );
  if (data().asset) {
    FixedAssetDetailResource.seedIfEmpty(
      qc,
      { businessId: data().businessId, id: params().fixedAssetId },
      data().asset!,
    );
  } else if (params().fixedAssetId === "new") {
    // Seed null into QueryClient for "new" to prevent SSR deferred stream hang
    const newKey = FixedAssetDetailResource.key({
      businessId: data().businessId,
      id: params().fixedAssetId,
    });
    if (qc.getQueryData(newKey) === undefined) {
      qc.setQueryData(newKey, null);
    }
  }

  const isNew = () => params().fixedAssetId === "new";
  const search = Route.useSearch();
  const initialMode: Mode = search().mode === "edit" ? "edit" : "view";
  const [mode, setMode] = createSignal<Mode>(initialMode);

  onMount(() => {
    if (initialMode === "edit") {
      const url = new URL(window.location.href);
      url.searchParams.delete("mode");
      window.history.replaceState(window.history.state, "", url.toString());
    }
  });

  const assetQuery = createQuery(() => ({
    ...FixedAssetDetailResource.options(
      { businessId: data().businessId, id: params().fixedAssetId },
      !isNew(),
    ),
    placeholderData: keepPreviousData,
  }));

  const accountsQuery = AccountsResource.use(() => ({
    businessId: data().businessId,
    limit: 200,
  }));

  const allAccounts = () => accountsQuery.data?.items ?? [];

  return (
    <div
      class={`flex flex-1 flex-col px-4 sm:px-6 pb-3 pt-10 ${
        !isNew() && mode() === "view"
          ? "@5xl/main:h-[calc(100svh-4.5rem)] @5xl/main:max-h-[calc(100svh-4.5rem)] @5xl/main:min-h-0 @5xl/main:overflow-auto"
          : ""
      }`}
    >
      <Show when={isNew()}>
        <FixedAssetEdit
          asset={null}
          businessId={data().businessId}
          allAccounts={allAccounts()}
          onCancel={() => navigate({ to: "/dashboard/fixed-assets" })}
          onSaved={() => navigate({ to: "/dashboard/fixed-assets" })}
        />
      </Show>
      <Show when={!isNew()}>
        <Show
          when={assetQuery.data}
          fallback={
            <div role="status" class="w-full animate-pulse">
              <div class="h-150 bg-muted rounded-lg w-full" />
            </div>
          }
        >
          {(asset) => (
            <Show
              when={mode() === "edit"}
              fallback={
                <FixedAssetView
                  asset={asset()}
                  businessId={data().businessId}
                  setMode={setMode}
                  onDeleted={() => {
                    FixedAssetDetailResource.remove(qc, {
                      businessId: data().businessId,
                      id: params().fixedAssetId,
                    });
                    // Remove the list cache so the table doesn't flash the
                    // deleted row before a background refetch.
                    qc.removeQueries({
                      queryKey: ["fixed-asset", data().businessId],
                    });
                    navigate({ to: "/dashboard/fixed-assets" });
                  }}
                />
              }
            >
              <FixedAssetEdit
                asset={asset()}
                businessId={data().businessId}
                allAccounts={allAccounts()}
                onCancel={() => setMode("view")}
                onSaved={() => {
                  FixedAssetDetailResource.invalidate(qc, {
                    businessId: data().businessId,
                    id: params().fixedAssetId,
                  });
                  setMode("view");
                }}
              />
            </Show>
          )}
        </Show>
      </Show>
    </div>
  );
}

// -- Delete Dialog -------------------------------------------------------------

function DeleteFixedAssetDialog(props: {
  asset: FixedAssetItem;
  businessId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted: () => void;
}) {
  const [isDeleting, setIsDeleting] = createSignal(false);

  async function handleDelete() {
    setIsDeleting(true);
    try {
      await deleteFixedAsset({
        data: { id: props.asset.id, businessId: props.businessId },
      });
      props.onDeleted();
    } catch {
      toast.error("Failed to delete fixed asset");
    } finally {
      setIsDeleting(false);
      props.onOpenChange(false);
    }
  }

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent class="max-w-md">
        <DialogHeader>
          <DialogTitle>Delete Fixed Asset</DialogTitle>
          <DialogDescription>
            This will permanently delete{" "}
            <span class="font-semibold">"{props.asset.name}"</span>. This action
            cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => props.onOpenChange(false)}
            disabled={isDeleting()}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting()}
          >
            <IconTrash class="mr-2 size-4" />
            {isDeleting() ? "Deleting..." : "Delete Asset"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -- Depreciation Chart --------------------------------------------------------

const DEPRECIATION_COLOR = { hex: "#5B8DEF", rgb: "91,141,239" };

function DepreciationChart(props: { asset: FixedAssetItem }) {
  const { colorMode } = useColorMode();

  const depreciationChartOptions = createMemo<ChartOptions>(() => {
    const dark = colorMode() === "dark";
    const tickColor = dark ? "hsl(240, 5%, 64.9%)" : "hsl(240, 3.8%, 30%)";
    const gridColor = dark
      ? "rgba(255,255,255,0.07)"
      : "rgba(128,128,128,0.08)";
    const tooltipBg = dark ? "rgba(50,50,60,0.95)" : "rgba(255,255,255,0.96)";
    const tooltipTextColor = dark ? "#fff" : "hsl(240, 10%, 10%)";
    return {
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 0, bottom: 0, left: 0, right: 4 } },
      scales: {
        y: {
          display: true,
          border: { display: false },
          grid: { color: gridColor },
          ticks: {
            maxTicksLimit: 4,
            color: tickColor,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            font: { size: 11, family: "'DM Mono', monospace" } as any,
            callback: (v: number | string) => {
              const n = +v;
              if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
              if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
              return `$${n}`;
            },
          },
        },
        x: {
          border: { display: false },
          grid: { display: false },
          ticks: {
            color: tickColor,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            font: { size: 11, family: "'DM Mono', monospace" } as any,
            maxRotation: 0,
          },
        },
      },
      plugins: {
        legend: {
          display: true,
          position: "bottom" as const,
          labels: {
            usePointStyle: true,
            pointStyle: "circle" as const,
            boxWidth: 6,
            boxHeight: 6,
            color: tickColor,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            font: { size: 12, family: "'DM Mono', monospace" } as any,
            padding: 20,
          },
        },
        title: { display: false },
        tooltip: {
          enabled: true,
          backgroundColor: tooltipBg,
          titleColor: tooltipTextColor,
          bodyColor: tooltipTextColor,
          borderColor: dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)",
          borderWidth: 1,
          padding: 8,
          cornerRadius: 6,
          callbacks: {
            label: (ctx) =>
              ` ${ctx.dataset.label}: $${(ctx.parsed.y ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
          },
        },
      },
    };
  });

  const chartData = createMemo<ChartData | null>(() => {
    const {
      purchaseCost,
      depreciationStartDate,
      usefulLifeYears,
      depreciationMethod,
      depreciationConvention,
    } = props.asset;

    if (
      purchaseCost == null ||
      !depreciationStartDate ||
      usefulLifeYears == null ||
      !depreciationMethod ||
      !depreciationConvention
    ) {
      return null;
    }

    try {
      const result = calculateDepreciation(
        purchaseCost,
        new Date(depreciationStartDate),
        usefulLifeYears,
        depreciationMethod as DepreciationMethod,
        depreciationConvention as DepreciationConvention,
      );

      const yearCount = result.current.length;
      if (yearCount === 0) return null;

      const labels = Array.from(
        { length: yearCount },
        (_, i) => `Year ${i + 1}`,
      );

      return {
        labels,
        datasets: [
          {
            label: "Depreciation",
            data: result.current,
            backgroundColor: `rgba(${DEPRECIATION_COLOR.rgb},0.85)`,
            borderRadius: 6,
            borderSkipped: false,
          },
          {
            label: "Accumulated",
            data: result.accumulated,
            backgroundColor: `rgba(${DEPRECIATION_COLOR.rgb},0.45)`,
            borderRadius: 6,
            borderSkipped: false,
          },
        ],
      };
    } catch {
      return null;
    }
  });

  return (
    <Show
      when={chartData()}
      fallback={
        <div class="flex h-32 items-center justify-center">
          <p class="text-[11px] text-muted-foreground">No depreciation data</p>
        </div>
      }
    >
      {(data) => (
        <div class="relative flex flex-col">
          {/* Chart */}
          <div class="h-80">
            <BarChart data={data()} options={depreciationChartOptions()} />
          </div>
        </div>
      )}
    </Show>
  );
}

// -- Depreciation Schedule Table ----------------------------------------------

type DepreciationScheduleRow = {
  year: number;
  depreciation: number;
  accumulated: number;
  remainingBasis: number;
};

const scheduleFormatCurrency = (v: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(v);

const scheduleColumns: ColumnDefExtended<DepreciationScheduleRow, unknown>[] = [
  {
    accessorKey: "year",
    header: () => <div class="pl-4">Year</div>,
    cellClass: "pl-4",
    footerClass: "pl-4",
    cell: (props) => (
      <div class="pl-4 font-medium">Year {props.row.original.year}</div>
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "depreciation",
    header: () => <div class="text-right">Depreciation</div>,
    cell: (props) => (
      <div class="text-right font-mono">
        {scheduleFormatCurrency(props.row.original.depreciation)}
      </div>
    ),
    enableSorting: false,
    enableSum: true,
    sumFn: (v) => scheduleFormatCurrency(v),
  },
  {
    accessorKey: "accumulated",
    header: () => <div class="text-right">Accumulated</div>,
    cell: (props) => (
      <div class="text-right font-mono">
        {scheduleFormatCurrency(props.row.original.accumulated)}
      </div>
    ),
    enableSorting: false,
    enableSum: true,
    sumFn: (v) => scheduleFormatCurrency(v),
  },
  {
    accessorKey: "remainingBasis",
    header: () => <div class="text-right pr-2 lg:pr-0">Remaining Basis</div>,
    footerClass: "pr-4 lg:pr-2",
    cell: (props) => (
      <div class="text-right font-mono pr-2 lg:pr-0">
        {scheduleFormatCurrency(props.row.original.remainingBasis)}
      </div>
    ),
    enableSorting: false,
    enableHiding: false,
  },
];

function DepreciationScheduleTable(props: { asset: FixedAssetItem }) {
  const rows = createMemo<DepreciationScheduleRow[]>(() => {
    const {
      purchaseCost,
      depreciationStartDate,
      usefulLifeYears,
      depreciationMethod,
      depreciationConvention,
    } = props.asset;

    if (
      purchaseCost == null ||
      !depreciationStartDate ||
      usefulLifeYears == null ||
      !depreciationMethod ||
      !depreciationConvention
    ) {
      return [];
    }

    try {
      const result = calculateDepreciation(
        purchaseCost,
        new Date(depreciationStartDate),
        usefulLifeYears,
        depreciationMethod as DepreciationMethod,
        depreciationConvention as DepreciationConvention,
      );

      return result.current.map((depreciation, i) => ({
        year: i + 1,
        depreciation,
        accumulated: result.accumulated[i] ?? 0,
        remainingBasis: result.unrecoveredBasis[i] ?? 0,
      }));
    } catch {
      return [];
    }
  });

  return (
    <Show
      when={rows().length > 0}
      fallback={
        <div class="flex h-32 items-center justify-center rounded-none border-x-0 border sm:rounded-2xl sm:border-x bg-card">
          <p class="text-[11px] text-muted-foreground">No depreciation data</p>
        </div>
      }
    >
      <DataTable
        data={rows()}
        columns={scheduleColumns}
        defaultShowAllRows
        hidePagination
        hideToolbar
      />
    </Show>
  );
}

// -- View ----------------------------------------------------------------------

function FixedAssetView(props: {
  asset: FixedAssetItem;
  businessId: string;
  setMode: (m: Mode) => void;
  onDeleted: () => void;
}) {
  const [deleteOpen, setDeleteOpen] = createSignal(false);

  const formatCurrency = (value: number | undefined) =>
    value != null
      ? new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(value)
      : "N/A";

  return (
    <div class="flex flex-1 flex-col gap-6">
      <StandardRouteDetailHeader
        icon={<IconTruck class="size-5" />}
        title={props.asset.name}
        badge={props.asset.depreciationMethod ?? undefined}
        date={
          props.asset.purchaseDate
            ? new Date(props.asset.purchaseDate)
            : undefined
        }
        menuContent={
          <>
            <DropdownMenuItem onClick={() => props.setMode("edit")}>
              <IconPencil class="size-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              class="text-destructive focus:text-destructive"
              onClick={() => setDeleteOpen(true)}
            >
              <IconTrash class="size-4" />
              Delete
            </DropdownMenuItem>
          </>
        }
      />

      <StandardRouteDetailMode mode="content">
        <StandardRouteDetailGrid>
          <StandardRouteDetailLeftColumn>
            <div class="space-y-6">
              <StandardRouteDetailCard
                icon={<IconWrench class="size-5" />}
                title="Asset Details"
                contentClass=""
                action={
                  <Tooltip>
                    <TooltipTrigger
                      as={Button<"button">}
                      variant="ghost"
                      size="icon"
                      class="size-7 text-muted-foreground hover:text-foreground"
                      aria-label="Edit fixed asset"
                      onClick={() => props.setMode("edit")}
                    >
                      <IconPencil class="size-4" />
                    </TooltipTrigger>
                    <TooltipContent>Edit</TooltipContent>
                  </Tooltip>
                }
              >
                {/* Purchase cost is the single most important accounting fact — make it the hero */}
                <div class="pb-5">
                  <p class="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">
                    Purchase Cost
                  </p>
                  <p class="mt-1.5 text-3xl font-semibold tabular-nums tracking-tight">
                    {formatCurrency(props.asset.purchaseCost)}
                  </p>
                </div>

                <div class="divide-y divide-border border-t">
                  <InfoRow
                    label="Purchase Date"
                    value={formatDisplayCalendarDate(props.asset.purchaseDate)}
                  />
                  <InfoRow
                    label="Placed in Service"
                    value={formatDisplayCalendarDate(
                      props.asset.depreciationStartDate,
                    )}
                  />
                  <InfoRow
                    label="Salvage Value"
                    value={formatCurrency(props.asset.salvageValue)}
                  />
                  <InfoRow
                    label="Useful Life"
                    value={
                      props.asset.usefulLifeYears != null
                        ? `${props.asset.usefulLifeYears} years`
                        : null
                    }
                  />
                  <InfoRow
                    label="Method"
                    value={props.asset.depreciationMethod}
                  />
                  <InfoRow
                    label="Convention"
                    value={props.asset.depreciationConvention}
                  />
                  <InfoRow
                    label="Accumulated Depreciation"
                    value={formatCurrency(props.asset.accumulatedDepreciation)}
                  />
                </div>
              </StandardRouteDetailCard>
            </div>
          </StandardRouteDetailLeftColumn>

          <StandardRouteDetailRightColumn>
            <StandardRouteDetailRightCard>
              <StandardRouteDetailRightCardHeader>
                <StandardRouteDetailTabs
                  defaultValue="depreciation"
                  class="flex flex-col"
                >
                  <TabsList class="w-full">
                    <TabsTrigger value="depreciation" class="flex-1">
                      <IconTruck class="mr-2 size-4" />
                      <span class="hidden sm:inline">Depreciation</span>
                    </TabsTrigger>
                    <TabsTrigger value="associations" class="flex-1">
                      <IconLink class="mr-2 size-4" />
                      <span class="hidden sm:inline">Associations</span>
                    </TabsTrigger>
                  </TabsList>

                  <StandardRouteDetailTabsStack>
                    <StandardRouteDetailTabsContent
                      value="depreciation"
                      class="overflow-auto"
                      forceMount
                    >
                      <div class="space-y-4">
                        <DepreciationChart asset={props.asset} />
                        <DepreciationScheduleTable asset={props.asset} />
                      </div>
                    </StandardRouteDetailTabsContent>

                    <StandardRouteDetailTabsContent
                      value="associations"
                      class="overflow-auto"
                      forceMount
                    >
                      <CardContent class="px-0">
                        <StandardRouteAssociationsPanel
                          source={{ type: "fixed-asset", id: props.asset.id }}
                          businessId={props.businessId}
                          tabs={[
                            {
                              type: "bank-account",
                              defaultVisibility: {
                                bankName: false,
                                accountCode: false,
                                description: false,
                                updatedAt: false,
                              },
                            },
                            {
                              type: "bank-transaction",
                              defaultVisibility: {
                                status: false,
                                institutionName: false,
                                mappedAccountCode: false,
                                bankAccountName: false,
                                updatedAt: false,
                              },
                            },
                            {
                              type: "journal-entry",
                              defaultVisibility: {
                                reference: false,
                                entries: false,
                              },
                            },
                            {
                              type: "document",
                              defaultVisibility: {
                                type: false,
                                vendor: false,
                                amount: false,
                              },
                            },
                            {
                              type: "worksheet",
                              defaultVisibility: {
                                description: false,
                                type: false,
                                updatedAt: false,
                              },
                            },
                            {
                              type: "vendor",
                              defaultVisibility: {
                                address: false,
                                website: false,
                                contactName: false,
                              },
                            },
                          ]}
                        />
                      </CardContent>
                    </StandardRouteDetailTabsContent>
                  </StandardRouteDetailTabsStack>
                </StandardRouteDetailTabs>
              </StandardRouteDetailRightCardHeader>
            </StandardRouteDetailRightCard>
          </StandardRouteDetailRightColumn>
        </StandardRouteDetailGrid>
      </StandardRouteDetailMode>

      <DeleteFixedAssetDialog
        asset={props.asset}
        businessId={props.businessId}
        open={deleteOpen()}
        onOpenChange={setDeleteOpen}
        onDeleted={props.onDeleted}
      />
    </div>
  );
}

// -- Edit / Create -------------------------------------------------------------

function FixedAssetEdit(props: {
  asset: FixedAssetItem | null;
  businessId: string;
  allAccounts: Account[];
  onCancel: () => void;
  onSaved: () => void;
}) {
  const isNew = () => props.asset === null;
  const [serverError, setServerError] = createSignal("");

  const form = useFixedAssetForm(props.businessId, props.asset, {
    onSuccess: () => {
      toast.success(
        isNew()
          ? "Fixed asset created successfully"
          : "Fixed asset updated successfully",
      );
      props.onSaved();
    },
    onError: (e) =>
      setServerError(
        e instanceof Error ? e.message : "An unexpected error occurred",
      ),
  });

  return (
    <StandardRouteDetailEditCard
      title={isNew() ? "Add Fixed Asset" : "Edit Fixed Asset"}
      description={
        isNew()
          ? "Create a new fixed asset record."
          : "Update the asset details. Changes apply immediately on save."
      }
      formId="fixed-asset-edit-form"
      onCancel={props.onCancel}
      saveLabel={isNew() ? "Add Fixed Asset" : "Save Changes"}
    >
      <form
        id="fixed-asset-edit-form"
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <Grid cols={1} colsMd={2} colsLg={3} class="gap-4">
          {/* Name */}
          <Col span={1} spanMd={2} spanLg={3}>
            <form.Field name="name">
              {(field) => (
                <FormField label="Name" required field={field()}>
                  <TextFieldInput
                    type="text"
                    placeholder="e.g. Company Truck"
                    value={field().state.value}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                    onBlur={field().handleBlur}
                  />
                </FormField>
              )}
            </form.Field>
          </Col>

          {/* Description */}
          <Col span={1} spanLg={3}>
            <form.Field name="description">
              {(field) => (
                <FormField label="Description" field={field()}>
                  <TextFieldInput
                    type="text"
                    placeholder="Optional description"
                    value={field().state.value ?? ""}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                    onBlur={field().handleBlur}
                  />
                </FormField>
              )}
            </form.Field>
          </Col>

          {/* Purchase Date */}
          <Col>
            <form.Field name="purchaseDate">
              {(field) => (
                <DatePickerField
                  label="Purchase Date"
                  value={field().state.value ?? ""}
                  onChange={(v) => field().handleChange(v)}
                  onBlur={field().handleBlur}
                  error={field().state.meta.errors[0]?.toString()}
                />
              )}
            </form.Field>
          </Col>

          {/* Depreciation Start Date */}
          <Col>
            <form.Field name="depreciationStartDate">
              {(field) => (
                <DatePickerField
                  label="Placed in Service"
                  value={field().state.value ?? ""}
                  onChange={(v) => field().handleChange(v)}
                  onBlur={field().handleBlur}
                  error={field().state.meta.errors[0]?.toString()}
                />
              )}
            </form.Field>
          </Col>

          {/* Purchase Cost */}
          <Col>
            <form.Field name="purchaseCost">
              {(field) => (
                <FormField label="Purchase Cost" required field={field()}>
                  <TextFieldInput
                    type="text"
                    inputmode="decimal"
                    placeholder="0.00"
                    value={field().state.value}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                    onBlur={field().handleBlur}
                  />
                </FormField>
              )}
            </form.Field>
          </Col>

          {/* Salvage Value */}
          <Col>
            <form.Field name="salvageValue">
              {(field) => (
                <FormField label="Salvage Value" field={field()}>
                  <TextFieldInput
                    type="text"
                    inputmode="decimal"
                    placeholder="0.00"
                    value={field().state.value}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                    onBlur={field().handleBlur}
                  />
                </FormField>
              )}
            </form.Field>
          </Col>

          {/* Useful Life */}
          <Col>
            <form.Field name="usefulLifeYears">
              {(field) => (
                <FormField label="Useful Life (years)" field={field()}>
                  <TextFieldInput
                    type="text"
                    inputmode="numeric"
                    placeholder="5"
                    value={field().state.value}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                    onBlur={field().handleBlur}
                  />
                </FormField>
              )}
            </form.Field>
          </Col>

          {/* Depreciation Method */}
          <Col>
            <form.Field name="depreciationMethod">
              {(field) => (
                <FormField label="Depreciation Method" field={field()}>
                  <MultiSelect
                    placeholder="Select method..."
                    searchPlaceholder="Search methods..."
                    options={DepreciationMethods}
                    value={
                      field().state.value ? [field().state.value as string] : []
                    }
                    onChange={(vals) =>
                      field().handleChange(
                        (vals[0] as DepreciationMethod) ?? undefined,
                      )
                    }
                    multiple={false}
                  />
                </FormField>
              )}
            </form.Field>
          </Col>

          {/* Depreciation Convention */}
          <Col>
            <form.Field name="depreciationConvention">
              {(field) => (
                <FormField label="Convention" field={field()}>
                  <MultiSelect
                    placeholder="Select convention..."
                    searchPlaceholder="Search conventions..."
                    options={DepreciationConventions}
                    value={
                      field().state.value ? [field().state.value as string] : []
                    }
                    onChange={(vals) =>
                      field().handleChange(
                        (vals[0] as DepreciationConvention) ?? undefined,
                      )
                    }
                    multiple={false}
                  />
                </FormField>
              )}
            </form.Field>
          </Col>

          {/* Asset Account */}
          <Col>
            <form.Field name="assetAccountId">
              {(field) => (
                <FormField label="Asset Account" field={field()}>
                  <MultiSelect
                    placeholder="Select asset account..."
                    searchPlaceholder="Search accounts..."
                    options={props.allAccounts.map((a) => ({
                      label: `${a.code} - ${a.name}`,
                      value: a.id,
                    }))}
                    value={
                      field().state.value != null
                        ? [field().state.value as string]
                        : []
                    }
                    onChange={(vals) =>
                      field().handleChange(vals[0] ?? undefined)
                    }
                    multiple={false}
                  />
                </FormField>
              )}
            </form.Field>
          </Col>

          {/* Contra Account */}
          <Col>
            <form.Field name="contraAccountId">
              {(field) => (
                <FormField label="Contra Account" field={field()}>
                  <MultiSelect
                    placeholder="Select contra account..."
                    searchPlaceholder="Search accounts..."
                    options={props.allAccounts.map((a) => ({
                      label: `${a.code} - ${a.name}`,
                      value: a.id,
                    }))}
                    value={
                      field().state.value != null
                        ? [field().state.value as string]
                        : []
                    }
                    onChange={(vals) =>
                      field().handleChange(vals[0] ?? undefined)
                    }
                    multiple={false}
                  />
                </FormField>
              )}
            </form.Field>
          </Col>

          {/* Expense Account */}
          <Col>
            <form.Field name="expenseAccountId">
              {(field) => (
                <FormField label="Expense Account" field={field()}>
                  <MultiSelect
                    placeholder="Select expense account..."
                    searchPlaceholder="Search accounts..."
                    options={props.allAccounts.map((a) => ({
                      label: `${a.code} - ${a.name}`,
                      value: a.id,
                    }))}
                    value={
                      field().state.value != null
                        ? [field().state.value as string]
                        : []
                    }
                    onChange={(vals) =>
                      field().handleChange(vals[0] ?? undefined)
                    }
                    multiple={false}
                  />
                </FormField>
              )}
            </form.Field>
          </Col>
        </Grid>

        <Show when={serverError()}>
          <p class="mt-4 text-sm text-destructive">{serverError()}</p>
        </Show>
      </form>
    </StandardRouteDetailEditCard>
  );
}

// -- Helpers -------------------------------------------------------------------

function InfoRow(props: { label: string; value: string | null | undefined }) {
  return (
    <div class="flex items-center justify-between py-3.5">
      <p class="text-sm text-muted-foreground">{props.label}</p>
      <h6 class="text-sm font-semibold">{props.value || "N/A"}</h6>
    </div>
  );
}

function FormField(props: {
  label: string;
  required?: boolean;
  field: {
    state: { meta: { errors: (string | { message: string } | undefined)[] } };
  };
  children: JSX.Element;
}) {
  const hasError = () => props.field.state.meta.errors.length > 0;
  const errorMessage = () => {
    const e = props.field.state.meta.errors[0];
    if (!e) return undefined;
    return typeof e === "string" ? e : e.message;
  };
  return (
    <TextField validationState={hasError() ? "invalid" : "valid"}>
      <TextFieldLabel>
        {props.label}
        <Show when={props.required}>
          <span class="text-destructive ml-0.5">*</span>
        </Show>
      </TextFieldLabel>
      {props.children}
      <TextFieldErrorMessage>{errorMessage()}</TextFieldErrorMessage>
    </TextField>
  );
}
